import { initializeApp, type FirebaseApp } from 'firebase/app';
import { initializeAppCheck, ReCaptchaEnterpriseProvider, type AppCheck } from 'firebase/app-check';
import { connectDatabaseEmulator, getDatabase, type Database } from 'firebase/database';
import { connectFirestoreEmulator, getFirestore, type Firestore } from 'firebase/firestore';
import { connectAuthEmulator, getAuth, type Auth } from 'firebase/auth';
import { connectStorageEmulator, getStorage, type FirebaseStorage } from 'firebase/storage';
import { connectFunctionsEmulator, getFunctions } from 'firebase/functions';

const APP_CHECK_SITE_KEY = '6LexFH0sAAAAAL7nNTBtuatkwtLc7A76xf3OOfGO';

const isLocalHost = () => {
    const t1 = ['localhost', '127.0.0.1'].includes(window.location.hostname);
    const t2 = window.location.hostname.startsWith('192.168');
    return t1 || t2;
}
export interface FirebaseConfig {
    apiKey: string;
    authDomain: string;
    projectId: string;
    storageBucket: string;
    messagingSenderId: string;
    appId: string;
    databaseURL: string;
}

export interface EmulatorConfig {
    enabled: boolean;
    host: string;
}

// Inizializza solo l'app Firebase
export function createFirebaseApp(config: FirebaseConfig): FirebaseApp {
    return initializeApp(config);
}

// Inizializza App Check con supporto per l'emulatore (debug token) e fallback automatico in produzione
export function createAppCheck(app: FirebaseApp, emulatorConfig?: { enabled: boolean }): AppCheck | null {
    // 1. Verifica se siamo in un ambiente browser (App Check non gira su Node)
    if (typeof window === 'undefined') return null;

    // 2. Se l'emulatore è attivo o siamo in localhost, abilitiamo il Debug Token
    const isLocal = isLocalHost();

    if (emulatorConfig?.enabled || isLocal) {
        // Questa riga dice a Firebase: "Cerca un token di debug invece di un token reCAPTCHA reale
        (self as any).FIREBASE_APPCHECK_DEBUG_TOKEN = true;
        console.info("🛡️ App Check: Debug Mode enabled");
    }

    return initializeAppCheck(app, {
        provider: new ReCaptchaEnterpriseProvider(APP_CHECK_SITE_KEY),
        isTokenAutoRefreshEnabled: true // Refresh automatico del token per lunghe sessioni
    });
}

// ReatimeDB
export function createRealtimeDatabase(app: FirebaseApp, emulatorConfig?: EmulatorConfig): Database {
    const rtdb = getDatabase(app);

    if (emulatorConfig?.enabled) {
        const host = emulatorConfig.host;
        console.info('Connecting to Realtime Database emulator at', `http://${host}:9000`);
        connectDatabaseEmulator(rtdb, host, 9000);
    }

    return rtdb;
}

// Firestore
export function createFirestore(app: FirebaseApp, emulatorConfig?: EmulatorConfig): Firestore {
    const firestore = getFirestore(app);

    if (emulatorConfig?.enabled) {
        const host = emulatorConfig.host;
        console.info('Connecting to Firestore emulator at', `http://${host}:8080`);
        connectFirestoreEmulator(firestore, host, 8080);
    }

    return firestore;
}

// Auth
export function createAuth(app: FirebaseApp, emulatorConfig?: EmulatorConfig): Auth {
    const auth = getAuth(app);

    if (emulatorConfig?.enabled) {
        const host = emulatorConfig.host;
        console.info('Connecting to Auth emulator at', `http://${host}:9099`);
        connectAuthEmulator(auth, `http://${host}:9099`, { disableWarnings: true });
    }

    return auth;
}

// Storage
export function createStorage(app: FirebaseApp, emulatorConfig?: EmulatorConfig): FirebaseStorage {
    const storage = getStorage(app);

    if (emulatorConfig?.enabled) {
        const host = emulatorConfig.host;
        console.info('Connecting to Storage emulator at', `http://${host}:9199`);
        connectStorageEmulator(storage, host, 9199);
    }

    return storage;
}

// Functions 
export function createFunctions(app: FirebaseApp, emulatorConfig?: EmulatorConfig) {
    const functions = getFunctions(app, 'europe-west1');

    if (emulatorConfig?.enabled) {
        const host = emulatorConfig.host;
        console.info('Connecting to Functions emulator at', `http://${host}:5001`);
        connectFunctionsEmulator(functions, host, 5001);
    }

    return functions;
}